import tw, { styled } from 'twin.macro';
import 'styled-components/macro';

import { LazyImage, LazyImageRenderProps } from 'component-lib/atoms/lazy-image/LazyImage';
import { Carousel } from 'play/molecules/carousel/Carousel';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';
import { ChallengeNoteType } from 'types/graphql';

import { ChallengeNote } from './ChallengeNote';
import { ResponsiveFrame } from './ResponsiveFrame';
import { ResponsiveImageFrame } from './ResponsiveImageFrame';

export type Note =
  | {
      id: string;
      type: ChallengeNoteType.Image;
      url: string;
    }
  | {
      id: string;
      type: ChallengeNoteType.Video;
      url: string;
    }
  | {
      id: string;
      type: ChallengeNoteType.Text;
      contents: string;
    };

export interface ChallengeNotesProps {
  activeIndex?: number;
  className?: string;
  notes: Note[];
  onActiveIndexChange: (newIndex: number) => void;
}

export const ChallengeNotes = ({ activeIndex, className, notes, onActiveIndexChange }: ChallengeNotesProps) => {
  const trackAmplitudeEvent = useTrackAmplitudeEvent();

  const onArrowClick = (index: number) => {
    onActiveIndexChange(index);

    const slideCount = notes.length;
    const slideNumber = index + 1;
    const slidesCompletedPct = Math.round((slideNumber / slideCount) * 10000) / 100;

    trackAmplitudeEvent({
      name: 'Lab - Notes - Navigated to slide',
      data: { slideCount, slideNumber, slidesCompletedPct, viaPagination: false },
    });
  };

  return notes.length > 0 ? (
    <Carousel className={className} activeIndex={activeIndex} onActiveIndexChange={onArrowClick}>
      {notes.map(note => {
        if (note.type === ChallengeNoteType.Image) {
          return (
            <LazyImage
              key={note.id}
              tw="block h-full rounded-xs w-full"
              alt="Image note"
              render={(props: LazyImageRenderProps<HTMLDivElement>) =>
                props.isReady ? <ResponsiveImageFrame image={props.image} innerRef={props.containerRef} /> : null
              }
              src={note.url}
            />
          );
        }

        if (note.type === ChallengeNoteType.Video) {
          const isExternalVideo = !note.url.includes('.instruqt.');

          return (
            <ResponsiveFrame key={note.id} aspectRatio={16 / 9}>
              {!isExternalVideo ? (
                // Internal video assets don't load on safari with iframe if files are too large, but video works
                <video tw="block h-full rounded-xs w-full" src={note.url} title="Video note" controls autoPlay />
              ) : (
                <iframe
                  tw="block h-full rounded-xs w-full"
                  allow="autoplay"
                  allowFullScreen
                  frameBorder={0}
                  src={note.url}
                  title="Video note"
                />
              )}
            </ResponsiveFrame>
          );
        }

        return (
          <NoteWrapper key={note.id}>
            <div tw="w-full max-h-full m-auto">
              <ChallengeNote content={note.contents} />
            </div>
          </NoteWrapper>
        );
      })}
    </Carousel>
  ) : (
    <div tw="flex items-center justify-center" className={className}>
      <h2 tw="display-3 text-white">Please wait while we set up the challenge.</h2>
    </div>
  );
};

const NoteWrapper = styled.div`
  ${tw`h-full flex overflow-auto`}

  &::-webkit-scrollbar {
    ${tw`w-1 h-1 bg-transparent`}
  }

  &::-webkit-scrollbar-thumb {
    ${tw`bg-gray-50 rounded border-2 border-solid border-transparent`}
    background-clip: content;
  }
`;
